import { resolutionOf } from "@/components/OliverBotV2/interactions/history.js";
import { savedActivity } from "@/components/OliverBotV2/utils/activityParts.js";
import { isConversationId } from "@/components/OliverBotV2/utils/oliverLinks.js";
import {
  DEFAULT_DARK_MODE,
  DEFAULT_THEME,
} from "@/components/OliverBotV2/theme/themes.js";
import { IN_FLIGHT } from "@/components/OliverBotV2/attachments/attachmentStatus.js";
import oliverClient, { isUpstreamUnavailable } from "@/lib/OliverClient";
import { loadFromStorage, saveToStorage } from "@/utils/storage.js";
import { sortBy, uniqBy } from "lodash";
import Swal from "sweetalert2";

// What an upload holds that must not be reactive state: the File (kept for Retry until the
// bytes are in S3), the request's AbortController (Remove cancels it) and a local preview URL
// for an image while it uploads.
const _uploads = {};
// Files the user removed: nothing still in flight for them may bring them back.
const _removed = new Set();
let _statusTimer = null;

// Files per message and per file. Limits the server enforces too.
export const MAX_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;
// A PUT or a `complete` that fails is tried twice more, after these pauses.
const RETRY_DELAYS_MS = [1000, 3000];
// One status call a second, only while a file is being read, for up to 20 files at once.
const STATUS_POLL_MS = 1000;
const STATUS_IDS_PER_CALL = 20;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Update a file's live state, unless the user removed it meanwhile.
const upsert = (commit, state, entry) => {
  if (!_removed.has(entry.id) && state.attachments[entry.id]) {
    commit("UPSERT_ATTACHMENT", entry);
  }
};

// What the server said about a refused file, as one sentence (a 422 carries a list).
const refusalText = (err, fallback = "it could not be attached.") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => d?.msg || String(d)).join("; ");
  }
  return fallback;
};

const fromServer = (att) => ({
  id: att.id,
  status: att.status,
  stage: att.stage ?? null,
  pages_total: att.pages_total ?? null,
  pages_done: att.pages_done ?? null,
  partial_pages: att.partial_pages ?? null,
  error: att.error ?? null,
  ...(att.url !== undefined && { url: att.url }),
  ...(att.original_file_name && { original_file_name: att.original_file_name }),
  ...(att.size != null && { size: att.size }),
  ...(att.mime_type && { mime_type: att.mime_type }),
});

// Oliver may use the web unless the clinician turns it off (the globe under the message box).
const DEFAULT_WEB_ACCESS = true;

// A toast rather than a modal: the appearance change already applied optimistically,
// and rolling it back is not worth interrupting whatever the user is doing.
const appearanceSaveFailed = (text) =>
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "error",
    title: text,
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });

// Covers the typer and the render throttle after `complete`, and skips the
// placeholder entirely when the chips arrive sooner (no flash).
const FOLLOW_UPS_SKELETON_DELAY_MS = 600;

// The stand-in assistant turn shown while waiting on a reply we are not
// streaming. One per chat, so it can be removed again by id.
export const PENDING_REPLY_ID = "pending-reply";

/** The role of the newest message, or null for an empty chat. */
const lastRole = (chatState) => {
  const messages = chatState?.messages || [];
  return messages.length ? messages[messages.length - 1].role : null;
};

/**
 * Which message to mark failed. A reload swaps the optimistic `user-…` id for
 * the server's, so the id we started with may no longer exist — fall back to
 * the newest user message rather than marking nothing.
 */
const failingMessageId = (chatState, preferredId) => {
  const messages = chatState?.messages || [];
  if (messages.some((m) => m.id === preferredId)) return preferredId;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "user") return messages[i].id;
  }
  return preferredId;
};

// Watchers for "is Oliver still generating for this chat", keyed by chat id.
const _generationWatchers = {};
// Resumes in flight, keyed by chat id: the chat list and an opened chat may both start one.
const _resumes = {};
// The typer drawing each resumed reply's text, and the text already handed to it.
const _resumeTypers = {};
// A resumed reply's text arrives in snapshots a poll apart; this spreads each over about that long.
const RESUMED_TYPING_FRAMES = 25;
// The most a finished resumed reply waits for its last text to finish typing.
const FINAL_TYPING_LIMIT_MS = 3000;

/**
 * Paints text a little at a time, so it reads as typing however it arrives: each frame shows a
 * `frames`-th of what is still waiting. `idle` resolves once everything pushed is shown; `stop`
 * drops what is waiting without painting it.
 */
function createTyper(paint, frames) {
  let pending = "";
  let shown = "";
  let frame = null;
  const waiting = [];
  const settle = () => waiting.splice(0).forEach((resolve) => resolve());

  const schedule = () => {
    if (frame === null && pending) frame = requestAnimationFrame(tick);
  };

  const tick = () => {
    frame = null;
    if (!pending) return settle();
    const take = Math.max(2, Math.ceil(pending.length / frames));
    shown += pending.slice(0, take);
    pending = pending.slice(take);
    paint(shown);
    if (pending) schedule();
    else settle();
  };

  return {
    push(text) {
      pending += text;
      schedule();
    },
    flush() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      if (pending) {
        shown += pending;
        pending = "";
        paint(shown);
      }
      settle();
    },
    stop() {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      pending = "";
      settle();
    },
    idle() {
      return pending
        ? new Promise((resolve) => waiting.push(resolve))
        : Promise.resolve();
    },
  };
}
// The probe also carries the steps and text so far; often enough that a reopened chat reads as live.
const GENERATION_POLL_INTERVAL_MS = 1500;
// A safety net, not a guess about backend timing: the server ends a turn that goes silent and
// closes out an abandoned one, so `running` always turns false on its own. Counted from the
// turn's last progress, never its start: a long reading runs for as long as it makes progress.
// Far above the server's own silence limit (6 minutes; a dead turn is noticed after 40s) so the
// client is never the first to give up on a reply that is still coming.
const GENERATION_WATCH_CEILING_MS = 20 * 60 * 1000;
// How long to keep trying to reach Oliver after losing the stream. A pod
// restart is seconds; this is generous enough to ride one out without leaving
// a user staring at a spinner if the service is genuinely gone.
const OLIVER_UNREACHABLE_BUDGET_MS = 90 * 1000;
const OLIVER_PROBE_RETRY_MS = 2000;
// A dropped stream may be re-requested this many times. The graph checkpoint
// means each attempt resumes rather than restarts, but a turn that keeps dying
// must eventually be reported instead of looping.
const LOST_STREAM_RETRY_LIMIT = 2;
// Consecutive failed probes tolerated mid-wait — the pod can blip again while
// we are already waiting on it. Twenty at the poll interval is ~30s, comfortably
// past the restarts seen in practice.
const GENERATION_PROBE_FAILURE_LIMIT = 20;
// What a chat reloads once a turn it waited on is over: the same page it opened with.
const MESSAGES_PAGE = 50;
// The server gives an answer's chips 20s after the answer; a little longer before giving up on them.
const FOLLOW_UPS_WAIT_MS = 25 * 1000;

// Activity labels ("CRM Records", "Searching patients") are produced by the
// backend and arrive on each progress event as `data.label`. The store renders
// them verbatim and keeps no naming dictionary of its own, so adding a backend
// tool or specialist never requires a frontend change.

/**
 * A turn's reasoning trail at its start. `phase` carries what the turn is doing
 * so the block reads correctly before any step or token has arrived; `outcome`
 * distinguishes a finished turn from one that failed or was stopped.
 */
const newActivity = (phase = "starting") => ({
  steps: [],
  startedAt: Date.now(),
  durationMs: null,
  done: false,
  phase,
  outcome: null,
});

/** The client a single message is scoped to, or null. */
function messagePatient(message) {
  const patientId = message?.context_metadata?.patient_id;
  if (message?.mode !== "patient" || !patientId) return null;
  return {
    id: patientId,
    first: String(message.context_metadata.patient_name || "").trim(),
    last: "",
    ...(message.context_metadata.user_id != null && {
      user_id: message.context_metadata.user_id,
    }),
  };
}

/**
 * The conversation list already carries each chat's last message, so a chat whose history
 * has not been fetched yet can still say who it is about — no blank gap while it loads.
 */
function listedLastMessage(state, chatId) {
  const listed = state.chatHistory?.pagination?.data || [];
  return listed.find((chat) => chat?.id === chatId)?.last_message || null;
}

/** Shared so the locally-added message and the one sent to the server cannot disagree. */
export function patientContextMetadata(patient) {
  if (!patient?.id) return null;
  return {
    patient_id: patient.id,
    patient_name: `${patient.first || ""} ${patient.last || ""}`.trim(),
    ...(patient.user_id != null && { user_id: patient.user_id }),
  };
}

/**
 * Vuex store module for OliverBotV2
 * Uses OliverClient for all API communication with Oliver backend
 */
export default {
  namespaced: true,

  state: {
    isCanceled: false,
    // Every attachment this tab is following, by id, in whichever chat it belongs to.
    attachments: {},
    // The ids in each chat's composer, in order; on send they move to the message.
    composerAttachments: {},
    // Files refused before upload, shown under the composer: { [chatId]: [{ name, reason }] }.
    attachmentNotices: {},
    // Files being created on the server, not yet in the composer: they count against the limit.
    attachmentsCreating: {},
    chatHistory: {
      loading: false,
      pagination: {
        total: 0,
        page: 0,
        itemsPerPage: 0,
        data: [],
        skip: 0,
        hasMore: true,
      },
    },
    chatStates: {},
    // chatId -> AbortController for in-flight answer streams. Streams keep running
    // across chat switches and are aborted only on intentional actions (delete).
    activeStreams: {},
    unseenCompletions: loadFromStorage("oliverUnseenCompletions", {}),
    selectedChat: null,
    // The conversation this tab was asked to restore via `?oliverChat=`. In-memory only
    // (never storage, which is shared between tabs). Shape:
    // { conversationId, status: 'loading'|'ready'|'failed', consumed }.
    // `consumed` is set once the user does something deliberate (Home, new chat, another
    // conversation), after which the param is stripped and the request is inert.
    restoreRequest: null,
    // Bumped per in-place citation navigation; a counter, so repeat clicks on the same
    // record still register.
    citationNavigations: 0,
    // chatId -> patient | null. Client mode chosen but not yet carried by a sent message.
    patientModeOverrides: {},
    isCreatingChat: false,
    isTranscribing: false,
    patientMode: null,
    theme: loadFromStorage("oliverTheme", DEFAULT_THEME),
    darkMode: loadFromStorage("oliverDarkMode", DEFAULT_DARK_MODE),
    webAccess: DEFAULT_WEB_ACCESS,
  },

  getters: {
    currentChatState: (state) => {
      if (!state.selectedChat) return null;
      return state.chatStates[state.selectedChat.id] || null;
    },
    currentMessages: (state, getters) => {
      return getters.currentChatState?.messages || [];
    },
    currentMessagesLoading: (state, getters) => {
      return getters.currentChatState?.loading || false;
    },
    currentIsWaitingForResponse: (state, getters) => {
      return getters.currentChatState?.isWaitingForResponse || false;
    },
    currentIsStreamingContent: (state, getters) => {
      return getters.currentChatState?.isStreamingContent || false;
    },
    currentIsPendingReconnect: (state, getters) => {
      return getters.currentChatState?.isPendingReconnect || false;
    },
    currentNextCursor: (state, getters) => {
      return getters.currentChatState?.nextCursor || null;
    },
    currentTheme: (state) => state.theme,
    isDarkMode: (state) => state.darkMode,
    currentMessageQueue: (state, getters) => {
      return getters.currentChatState?.messageQueue ?? [];
    },
    currentIsQueuePaused: (state, getters) => {
      return getters.currentChatState?.isQueuePaused ?? false;
    },
    // The message whose trail the drawer's live Activity board shows (the same
    // per-message `activity` object the reasoning block renders): the turn in
    // flight from the moment it starts, otherwise the last message with steps,
    // so the board keeps showing the just-finished turn while Oliver is idle.
    currentActivityMessage: (state, getters) => {
      const messages = getters.currentChatState?.messages || [];
      const inFlight = state.activeStreams[state.selectedChat?.id]?.messageId;
      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        if (
          message.id === inFlight ||
          message.id === PENDING_REPLY_ID ||
          message.activity?.steps?.length
        )
          return message;
      }
      return null;
    },
    currentActivity: (state, getters) => {
      return getters.currentActivityMessage?.activity || null;
    },
    isChatStreaming: (state) => (chatId) => {
      const chatState = state.chatStates[chatId];
      return Boolean(
        state.activeStreams[chatId] ||
          chatState?.isWaitingForResponse ||
          chatState?.isStreamingContent ||
          chatState?.isPendingReconnect
      );
    },
    hasUnseenCompletion: (state) => (chatId) =>
      Boolean(state.unseenCompletions[chatId]),
    // A conversation's client, from its newest message that settles the question.
    // A client on ANY message counts — a welcome-card summary is stored as "assistant".
    // Only a USER message turns it off: the backend's failure placeholder is an assistant
    // message with no client and must not read as a deliberate "off".
    conversationPatientContext: (state) => (chatId) => {
      const messages = state.chatStates[chatId]?.messages || [];
      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i];
        // No `mode` field at all means it has not been through the server yet.
        if (!message || !("mode" in message)) continue;

        const patient = messagePatient(message);
        if (patient) return patient;
        if (message.role === "user") return null;
      }

      // Loaded history settled nothing — the list's copy of the last message still can.
      const listed = listedLastMessage(state, chatId);
      return listed && "mode" in listed ? messagePatient(listed) : null;
    },
    // `getChatMessages` initialises `{ messages: [] }` before the request returns, so an
    // empty conversation is indistinguishable from an unloaded one until this is true.
    chatHistoryKnown: (state) => (chatId) => {
      if (!chatId) return false;
      const chatState = state.chatStates[chatId];
      return Boolean(chatState) && !chatState.loading;
    },
    // Does this conversation define its own client mode — an explicit choice, or any
    // message that has been through the server? False while its history is unloaded, so
    // the page can still seed a new chat.
    patientModeIsOwned: (state) => (chatId) => {
      if (!chatId) return false;
      if (
        Object.prototype.hasOwnProperty.call(state.patientModeOverrides, chatId)
      ) {
        return true;
      }
      const chatState = state.chatStates[chatId];
      if (chatState?.messages.some((message) => message && "mode" in message)) {
        return true;
      }
      const listed = listedLastMessage(state, chatId);
      return Boolean(listed && "mode" in listed);
    },
    // An unsent toggle wins over message history.
    patientContextFor: (state, getters) => (chatId) => {
      if (!chatId) return null;
      if (
        Object.prototype.hasOwnProperty.call(state.patientModeOverrides, chatId)
      ) {
        return state.patientModeOverrides[chatId];
      }
      return getters.conversationPatientContext(chatId);
    },
    currentAttachmentDrafts: (state) => {
      if (!state.selectedChat) return [];
      return (state.composerAttachments[state.selectedChat.id] || [])
        .map((id) => state.attachments[id])
        .filter(Boolean);
    },
    currentAttachmentsCreating: (state) => {
      if (!state.selectedChat) return 0;
      return state.attachmentsCreating[state.selectedChat.id] || 0;
    },
    currentAttachmentNotices: (state) => {
      if (!state.selectedChat) return [];
      return state.attachmentNotices[state.selectedChat.id] || [];
    },
  },

  mutations: {
    SET_CHAT_HISTORY_LOADING: (state, data) => {
      state.chatHistory.loading = data;
    },
    SET_CHAT_HISTORY_PAGINATION: (state, data) => {
      state.chatHistory.pagination = {
        ...state.chatHistory.pagination,
        ...data,
      };
    },
    ADD_CHAT_TO_HISTORY: (state, chat) => {
      const existingIndex = state.chatHistory.pagination.data.findIndex(
        (c) => c.id === chat.id
      );

      if (existingIndex !== -1) {
        state.chatHistory.pagination.data.splice(existingIndex, 1);
        state.chatHistory.pagination.data = [
          chat,
          ...state.chatHistory.pagination.data,
        ];
      } else {
        state.chatHistory.pagination.data = [
          chat,
          ...state.chatHistory.pagination.data,
        ];
        state.chatHistory.pagination.total += 1;
      }
    },
    INITIALIZE_CHAT_STATE: (state, chatId) => {
      if (!state.chatStates[chatId]) {
        state.chatStates[chatId] = {
          messages: [],
          loading: false,
          isWaitingForResponse: false,
          isStreamingContent: false,
          nextCursor: null,
          limit: null,
          messageQueue: [],
          isQueuePaused: false,
          isPendingReconnect: false,
        };
      }
    },
    SET_CHAT_MESSAGES_LOADING: (state, { chatId, loading }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].loading = loading;
    },
    SET_CHAT_MESSAGES_PAGINATION: (
      state,
      { chatId, nextCursor, limit, data }
    ) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].messages = data;
      state.chatStates[chatId].nextCursor = nextCursor;
      state.chatStates[chatId].limit = limit;
    },
    SET_SELECTED_CHAT: (state, data) => {
      state.selectedChat = data;
    },
    NOTE_CITATION_NAVIGATION: (state) => {
      state.citationNavigations += 1;
    },
    SET_RESTORE_REQUEST: (state, data) => {
      state.restoreRequest = data;
    },
    SET_RESTORE_STATUS: (state, status) => {
      if (!state.restoreRequest) return;
      state.restoreRequest = { ...state.restoreRequest, status };
    },
    SET_PATIENT_MODE_OVERRIDE: (state, { chatId, patient }) => {
      state.patientModeOverrides = {
        ...state.patientModeOverrides,
        [chatId]: patient,
      };
    },
    CLEAR_PATIENT_MODE_OVERRIDE: (state, chatId) => {
      if (
        !Object.prototype.hasOwnProperty.call(
          state.patientModeOverrides,
          chatId
        )
      ) {
        return;
      }
      const next = { ...state.patientModeOverrides };
      delete next[chatId];
      state.patientModeOverrides = next;
    },
    CONSUME_RESTORE_REQUEST: (state) => {
      if (!state.restoreRequest) return;
      state.restoreRequest = { ...state.restoreRequest, consumed: true };
    },
    SET_WAITING_FOR_RESPONSE: (state, { chatId, value }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].isWaitingForResponse = value;
    },
    SET_STREAMING_CONTENT: (state, { chatId, value }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].isStreamingContent = value;
    },
    SET_CREATING_CHAT: (state, value) => {
      state.isCreatingChat = value;
    },
    SET_TRANSCRIBING: (state, value) => {
      state.isTranscribing = value;
    },
    // Stamp an empty trail on the turn the moment it starts, before any step or
    // token arrives, so the reasoning block exists from the click rather than
    // appearing several seconds later when the first tool runs.
    BEGIN_TURN_ACTIVITY: (state, { chatId, messageId, phase }) => {
      const chatState = state.chatStates[chatId];
      if (!chatState) return;
      chatState.messages = [
        ...chatState.messages,
        {
          id: messageId,
          role: "assistant",
          content: "",
          created_at: new Date().toISOString(),
          activity: newActivity(phase),
        },
      ];
    },
    // A turn running on the server, drawn from what it has shown so far: its steps, the answer
    // text it has written, and its real start, so a reopened chat reads as the live one did.
    SHOW_RESUMED_TURN: (state, { chatId, progress, startedAt }) => {
      const chatState = state.chatStates[chatId];
      if (!chatState) return;
      const steps = (progress?.steps || [])
        .filter((s) => s.status !== "failed")
        .map(({ id, kind, status, label }) => ({ id, kind, status, label }));
      const text = progress?.text || "";
      const messages = chatState.messages;
      const index = messages.findIndex((msg) => msg.id == PENDING_REPLY_ID);
      const previous = index === -1 ? null : messages[index];
      const began = startedAt || previous?.activity?.startedAt || Date.now();
      const lastStep = Math.max(
        0,
        ...(progress?.steps || []).map((s) => (s.timestamp || 0) * 1000)
      );
      const phase = text ? "writing" : steps.length ? "tool" : "resuming";
      const message = {
        id: PENDING_REPLY_ID,
        role: "assistant",
        created_at: new Date().toISOString(),
        ...previous,
        // The typer draws the text; a snapshot only moves the steps and the clock.
        content: previous?.content || "",
        activity: {
          ...newActivity(phase),
          steps,
          startedAt: began,
          // As a live turn keeps it: worked until the answer began.
          durationMs: text && lastStep > began ? lastStep - began : null,
        },
      };
      if (index === -1) messages.push(message);
      else messages[index] = message;
      chatState.messages = [...messages];
    },
    // What the turn is doing right now; the wording lives in the component. The
    // time worked is the time until the answer began, as the saved reply keeps it.
    SET_ACTIVITY_PHASE: (state, { chatId, messageId, phase }) => {
      const chatState = state.chatStates[chatId];
      if (!chatState) return;
      const index = chatState.messages.findIndex((msg) => msg.id == messageId);
      if (index === -1) return;
      const message = chatState.messages[index];
      const activity = message.activity;
      if (!activity || activity.phase === phase) return;

      const answerBegan = phase === "writing" && activity.durationMs == null;
      chatState.messages[index] = {
        ...message,
        activity: {
          ...activity,
          phase,
          ...(answerBegan && {
            durationMs: Date.now() - (activity.startedAt || Date.now()),
          }),
        },
      };
      chatState.messages = [...chatState.messages];
    },
    STORE_CHAT_MESSAGE: (state, { chatId, message }) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id == message.id);
      if (messageIndex == -1) {
        const messageWithStatus = {
          ...message,
          status: message.role === "user" ? "pending" : "sent",
        };
        state.chatStates[chatId].messages = [...messages, messageWithStatus];
      }
    },
    SET_MESSAGE_STATUS: (
      state,
      { chatId, messageId, status, error = null }
    ) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id === messageId);

      if (messageIndex !== -1) {
        messages[messageIndex].status = status;
        if (error) {
          messages[messageIndex].error = error;
        }
      }
    },
    // Matched by the saved id too: a reload or recovery may have replaced the streamed message.
    SET_MESSAGE_FOLLOW_UPS: (state, { chatId, messageId, followUps }) => {
      const chat = state.chatStates[chatId];
      if (!chat || !messageId) return;
      const index = chat.messages.findIndex(
        (m) => m.server_id === messageId || m.id === messageId
      );
      if (index === -1) return;
      chat.messages[index] = {
        ...chat.messages[index],
        followUps,
        followUpsPending: false,
      };
      chat.messages = [...chat.messages];
    },
    SET_MESSAGE_FOLLOW_UPS_PENDING: (state, { chatId, messageId, pending }) => {
      const chat = state.chatStates[chatId];
      if (!chat || !messageId) return;
      const index = chat.messages.findIndex(
        (m) => m.server_id === messageId || m.id === messageId
      );
      if (index === -1) return;
      chat.messages[index] = {
        ...chat.messages[index],
        followUpsPending: pending,
      };
      chat.messages = [...chat.messages];
    },
    UPDATE_CHAT_MESSAGE: (state, { chatId, message }) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      const messageIndex = messages.findIndex((msg) => msg.id == message.id);

      if (messageIndex === -1) {
        // eslint-disable-next-line no-unused-vars
        const { replace, ...messageData } = message;
        messages.push({
          ...messageData,
          content: messageData.content || "",
        });
        state.chatStates[chatId].messages = [...messages];
        return;
      }

      const existingMessage = messages[messageIndex];

      if (message.replace) {
        // eslint-disable-next-line no-unused-vars
        const { replace, ...updates } = message;
        messages[messageIndex] = {
          ...existingMessage,
          ...updates,
          content:
            updates.content !== undefined
              ? updates.content
              : existingMessage.content,
        };
      } else {
        const deltaContent = message.content || "";
        messages[messageIndex] = {
          ...existingMessage,
          content: (existingMessage.content || "") + deltaContent,
        };
      }
      state.chatStates[chatId].messages = [...messages];
    },
    // Insert or update one step on the in-flight assistant message, correlating
    // by (id, kind) so a tool's start→done collapses into a single entry. A step
    // that failed leaves the trail: it did no work the reader needs to see.
    // BEGIN_TURN_ACTIVITY normally creates the message first; the fallback below
    // stays so a missing BEGIN degrades rather than dropping steps.
    UPSERT_ACTIVITY_STEP: (state, { chatId, messageId, step }) => {
      const chatState = state.chatStates[chatId];
      if (!chatState) return;
      const messages = chatState.messages;

      let index = messages.findIndex((msg) => msg.id == messageId);
      if (index === -1) {
        messages.push({
          id: messageId,
          role: "assistant",
          content: "",
          created_at: new Date().toISOString(),
          activity: newActivity(),
        });
        index = messages.length - 1;
      }

      const message = messages[index];
      const activity = message.activity || newActivity();
      const steps = activity.steps.slice();
      const existing = steps.findIndex(
        (s) => s.id === step.id && s.kind === step.kind
      );
      const merged = {
        id: step.id,
        kind: step.kind,
        status: step.status,
        label: step.label,
      };
      if (step.status === "failed") {
        if (existing !== -1) steps.splice(existing, 1);
      } else if (existing === -1) steps.push(merged);
      else steps[existing] = merged;

      messages[index] = { ...message, activity: { ...activity, steps } };
      chatState.messages = [...messages];
    },
    // Close out a message's trail when the turn ends, keeping the steps that
    // finished, as the saved reply does. `outcome` separates a finished turn
    // from one that failed or was stopped: the steps look the same either way,
    // the line does not.
    FINALIZE_MESSAGE_ACTIVITY: (
      state,
      { chatId, messageId, outcome = "done" }
    ) => {
      const chatState = state.chatStates[chatId];
      if (!chatState) return;
      const messages = chatState.messages;
      const index = messages.findIndex((msg) => msg.id == messageId);
      if (index === -1) return;

      const message = messages[index];
      if (!message.activity) return;

      const { activity } = message;
      messages[index] = {
        ...message,
        activity: {
          ...activity,
          steps: activity.steps.filter((s) => s.status === "done"),
          done: true,
          outcome,
          phase: outcome,
          // A stopped turn says when it stopped; a finished one, when its answer began.
          durationMs:
            (outcome === "done" && activity.durationMs) ||
            Date.now() - (activity.startedAt || Date.now()),
        },
      };
      chatState.messages = [...messages];
    },
    SET_MESSAGE_FEEDBACK: (state, { chatId, messageId, feedback }) => {
      if (!state.chatStates[chatId]) return;
      const messages = state.chatStates[chatId].messages;
      // Match on the local id OR the persisted server_id, so feedback on a
      // just-streamed answer (whose local id is a temp id) still lands.
      const messageIndex = messages.findIndex(
        (msg) => msg.id == messageId || msg.server_id == messageId
      );
      if (messageIndex === -1) return;

      messages[messageIndex] = {
        ...messages[messageIndex],
        feedback: feedback || null,
      };
      state.chatStates[chatId].messages = [...messages];
    },
    DELETE_CHAT_MESSAGE: (state, { chatId, messageId }) => {
      if (!state.chatStates[chatId]) return;
      state.chatStates[chatId].messages = state.chatStates[
        chatId
      ].messages.filter((msg) => msg.id != messageId);
    },
    REMOVE_CHAT_FROM_HISTORY: (state, chatId) => {
      state.chatHistory.pagination.data =
        state.chatHistory.pagination.data.filter((chat) => chat.id !== chatId);
      if (state.chatHistory.pagination.total > 0) {
        state.chatHistory.pagination.total -= 1;
      }
      if (state.selectedChat?.id === chatId) {
        state.selectedChat = null;
      }
      if (state.chatStates[chatId]) {
        delete state.chatStates[chatId];
      }
      if (state.activeStreams[chatId]) {
        delete state.activeStreams[chatId];
      }
    },
    SET_CHAT_TITLE: (state, { chatId, title }) => {
      const chatIndex = state.chatHistory.pagination.data.findIndex(
        (chat) => chat.id == chatId
      );
      if (chatIndex != -1) {
        state.chatHistory.pagination.data[chatIndex].title = title;
      }
      state.chatHistory.pagination.data = [
        ...state.chatHistory.pagination.data,
      ];

      if (state.selectedChat && state.selectedChat.id == chatId) {
        state.selectedChat = {
          ...state.selectedChat,
          title: title,
        };
      }
    },
    RESET_CHAT_MESSAGES: (state, chatId) => {
      if (chatId) {
        if (state.chatStates[chatId]) {
          delete state.chatStates[chatId];
        }
        return;
      }
      // No chatId = reset all chats, but keep any chat that is still generating
      // output so background streaming is not lost when the user returns to the
      // welcome/home view.
      const preserved = {};
      for (const id of Object.keys(state.chatStates)) {
        const cs = state.chatStates[id];
        if (
          state.activeStreams[id] ||
          cs?.isWaitingForResponse ||
          cs?.isStreamingContent
        ) {
          preserved[id] = cs;
        }
      }
      state.chatStates = preserved;
    },
    SET_PATIENT_MODE: (state, data) => {
      state.patientMode = data;
    },
    SET_THEME: (state, theme) => {
      state.theme = theme;
      saveToStorage("oliverTheme", theme);
    },
    SET_DARK_MODE: (state, value) => {
      state.darkMode = value;
      saveToStorage("oliverDarkMode", value);
    },
    SET_WEB_ACCESS: (state, value) => {
      state.webAccess = value;
    },
    ENQUEUE_MESSAGE: (state, { chatId, message }) => {
      const chat = state.chatStates[chatId];
      if (chat) chat.messageQueue.push(message);
    },
    DEQUEUE_MESSAGE: (state, { chatId }) => {
      state.chatStates[chatId]?.messageQueue.shift();
    },
    REMOVE_QUEUED_MESSAGE: (state, { chatId, index }) => {
      state.chatStates[chatId]?.messageQueue.splice(index, 1);
    },
    SET_PENDING_RECONNECT: (state, { chatId, value }) => {
      if (state.chatStates[chatId]) {
        state.chatStates[chatId].isPendingReconnect = value;
      }
    },
    SET_QUEUE_PAUSED: (state, { chatId, value }) => {
      if (state.chatStates[chatId]) {
        state.chatStates[chatId].isQueuePaused = value;
      }
    },
    SET_ACTIVE_STREAM: (state, { chatId, controller, messageId }) => {
      // Keep the in-flight message id so an abort can finalize its activity
      // trail (the abort path skips onComplete/onError).
      state.activeStreams[chatId] = { controller, messageId };
    },
    CLEAR_ACTIVE_STREAM: (state, chatId) => {
      if (state.activeStreams[chatId]) {
        delete state.activeStreams[chatId];
      }
    },
    SET_UNSEEN_COMPLETION: (state, { chatId, value }) => {
      if (value) {
        state.unseenCompletions[chatId] = true;
      } else {
        delete state.unseenCompletions[chatId];
      }
      saveToStorage("oliverUnseenCompletions", state.unseenCompletions);
    },
    UPSERT_ATTACHMENT: (state, entry) => {
      state.attachments[entry.id] = {
        ...(state.attachments[entry.id] || {}),
        ...entry,
      };
    },
    REMOVE_ATTACHMENT: (state, id) => {
      delete state.attachments[id];
      for (const chatId of Object.keys(state.composerAttachments)) {
        state.composerAttachments[chatId] = state.composerAttachments[
          chatId
        ].filter((x) => x !== id);
      }
    },
    ADD_TO_COMPOSER: (state, { chatId, id, replacing }) => {
      const ids = state.composerAttachments[chatId] || [];
      const at = replacing ? ids.indexOf(replacing) : -1;
      state.composerAttachments[chatId] =
        at >= 0
          ? [...ids.slice(0, at), id, ...ids.slice(at + 1)]
          : [...ids, id];
    },
    COUNT_ATTACHMENT_CREATING: (state, { chatId, delta }) => {
      state.attachmentsCreating[chatId] = Math.max(
        0,
        (state.attachmentsCreating[chatId] || 0) + delta
      );
    },
    CLEAR_COMPOSER: (state, chatId) => {
      state.composerAttachments[chatId] = [];
    },
    SET_ATTACHMENT_NOTICES: (state, { chatId, notices }) => {
      state.attachmentNotices[chatId] = notices;
    },
  },

  actions: {
    /**
     * Attach files to a chat's composer: each gets its id from the server first, then
     * uploads with progress and is read by the server when its upload completes.
     */
    addAttachments({ dispatch }, { chatId, files }) {
      return Promise.all(
        files.map((file) => dispatch("_uploadAttachment", { chatId, file }))
      );
    },

    noteRefusedFiles({ commit, state }, { chatId, notices }) {
      commit("SET_ATTACHMENT_NOTICES", {
        chatId,
        notices: [...(state.attachmentNotices[chatId] || []), ...notices],
      });
    },

    dismissAttachmentNotices({ commit }, chatId) {
      commit("SET_ATTACHMENT_NOTICES", { chatId, notices: [] });
    },

    async _uploadAttachment(
      { commit, dispatch, state },
      { chatId, file, replacing }
    ) {
      commit("COUNT_ATTACHMENT_CREATING", { chatId, delta: 1 });
      let created;
      try {
        created = (
          await oliverClient.createAttachment({ conversationId: chatId, file })
        ).data;
      } catch (err) {
        dispatch("noteRefusedFiles", {
          chatId,
          notices: [{ name: file.name, reason: refusalText(err) }],
        });
        if (replacing)
          upsert(commit, state, { id: replacing, status: "failed" });
        return null;
      } finally {
        commit("COUNT_ATTACHMENT_CREATING", { chatId, delta: -1 });
      }

      const id = created.id;
      const upload = {
        file,
        controller: new AbortController(),
        previewUrl: file.type?.startsWith("image/")
          ? URL.createObjectURL(file)
          : null,
      };
      _uploads[id] = upload;
      commit("UPSERT_ATTACHMENT", {
        ...fromServer(created.attachment),
        chatId,
        status: "uploading",
        progress: 0,
        preview_url: upload.previewUrl,
        original_file_name: file.name,
        size: file.size,
        mime_type: file.type || created.attachment.mime_type,
      });
      commit("ADD_TO_COMPOSER", { chatId, id, replacing });

      let percent = 0;
      let uploaded = false;
      for (
        let attempt = 0;
        attempt <= RETRY_DELAYS_MS.length && !_removed.has(id);
        attempt++
      ) {
        try {
          await oliverClient.putAttachment(created.upload_url, file, {
            // The type the URL was signed with, or S3 refuses the upload.
            contentType: created.attachment.mime_type,
            signal: upload.controller.signal,
            onProgress: (fraction) => {
              // One commit per whole percent, not per progress event.
              const now = Math.floor(fraction * 100);
              if (now === percent) return;
              percent = now;
              upsert(commit, state, { id, progress: fraction });
            },
          });
          uploaded = true;
          break;
        } catch (err) {
          if (err?.name === "AbortError") return null;
          if (attempt < RETRY_DELAYS_MS.length) {
            await sleep(RETRY_DELAYS_MS[attempt]);
          }
        }
      }
      if (_removed.has(id)) return null;

      // The bytes are in S3: the File is kept only while an upload could need it again.
      if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
      if (uploaded) delete _uploads[id];
      upsert(commit, state, {
        id,
        progress: 1,
        preview_url: null,
        upload_failed: !uploaded,
      });
      await dispatch("_completeAttachment", { id, uploaded });
      return id;
    },

    /**
     * Tell the server the upload finished (or gave up). Retried like the upload: a repeated
     * call is safe, the server reads a file once. If it still cannot be reached, the chip
     * says so and Retry asks again, rather than leaving the file waiting for an hour.
     */
    async _completeAttachment({ commit, dispatch, state }, { id, uploaded }) {
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
        if (_removed.has(id)) return;
        try {
          const response = await oliverClient.completeAttachment(id, uploaded);
          upsert(commit, state, {
            ...fromServer(response.data),
            complete_failed: false,
          });
          dispatch("_followStatuses");
          return;
        } catch (err) {
          if (err?.response?.status === 404) return;
          if (attempt < RETRY_DELAYS_MS.length) {
            await sleep(RETRY_DELAYS_MS[attempt]);
          }
        }
      }
      upsert(commit, state, {
        id,
        status: "failed",
        error: "The upload could not be confirmed.",
        complete_failed: uploaded,
      });
    },

    /** Remove a file in any state: cancel its upload, delete it and its stored copy. */
    async removeAttachment({ commit }, id) {
      _removed.add(id);
      const upload = _uploads[id];
      if (upload) {
        upload.controller.abort();
        if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
        delete _uploads[id];
      }
      commit("REMOVE_ATTACHMENT", id);
      try {
        await oliverClient.deleteAttachment(id);
      } catch (err) {
        // Gone already, or the network blipped: the server forgets an unsent file anyway.
      }
    },

    /**
     * Read a failed file again, from the composer. One whose upload never finished is
     * uploaded again from the File this tab kept, in the same place; one whose upload could
     * not be confirmed is confirmed again; one the server could not read is read again.
     */
    async retryAttachment({ commit, dispatch, state }, { chatId, id }) {
      const entry = state.attachments[id];
      const inComposer = (state.composerAttachments[chatId] || []).includes(id);
      if (!entry || entry.status !== "failed" || !inComposer) return;
      // Busy at once, so a second click finds nothing to retry.
      upsert(commit, state, {
        id,
        status: "pending",
        stage: "reading",
        error: null,
      });
      const kept = _uploads[id]?.file;
      if (entry.upload_failed && kept) {
        const replacement = await dispatch("_uploadAttachment", {
          chatId,
          file: kept,
          replacing: id,
        });
        if (replacement) await dispatch("removeAttachment", id);
        return;
      }
      if (entry.complete_failed) {
        await dispatch("_completeAttachment", { id, uploaded: true });
        return;
      }
      try {
        const response = await oliverClient.retryAttachment(id);
        upsert(commit, state, fromServer(response.data));
        dispatch("_followStatuses");
      } catch (err) {
        upsert(commit, state, {
          id,
          status: "failed",
          error: refusalText(err, "Couldn't read this file right now."),
        });
      }
    },

    /** The composer's files, handed to the message being sent; the composer clears. */
    takeComposerAttachments({ commit, getters, state }) {
      const chatId = state.selectedChat?.id;
      const drafts = getters.currentAttachmentDrafts;
      if (chatId) commit("CLEAR_COMPOSER", chatId);
      if (chatId) commit("SET_ATTACHMENT_NOTICES", { chatId, notices: [] });
      return drafts.map((d) => ({
        id: d.id,
        original_file_name: d.original_file_name,
        size: d.size,
        mime_type: d.mime_type,
        url: d.url ?? null,
      }));
    },

    /** A message that could not be sent gives its files back to the composer. */
    returnToComposer({ commit, state }, { chatId, attachments }) {
      for (const attachment of attachments || []) {
        if (state.attachments[attachment.id]) {
          commit("ADD_TO_COMPOSER", { chatId, id: attachment.id });
        }
      }
    },

    /** A queued message removed before it was sent takes its files with it. */
    async removeQueuedMessage({ commit, dispatch, state }, { chatId, index }) {
      const queued = state.chatStates[chatId]?.messageQueue[index];
      commit("REMOVE_QUEUED_MESSAGE", { chatId, index });
      await Promise.all(
        (queued?.attachments || []).map((a) =>
          dispatch("removeAttachment", a.id)
        )
      );
    },

    /** Files of loaded messages that are still being read: keep their chips live. */
    followAttachments({ commit, dispatch, state }, messages) {
      let any = false;
      for (const message of messages || []) {
        for (const att of message.attachments || []) {
          if (IN_FLIGHT.has(att.status) && !state.attachments[att.id]) {
            commit("UPSERT_ATTACHMENT", fromServer(att));
            any = true;
          }
        }
      }
      if (any) dispatch("_followStatuses");
    },

    /** One status call a second for every file being read, until none is. */
    _followStatuses({ commit, state }) {
      if (_statusTimer) return;
      const tick = async () => {
        const ids = Object.values(state.attachments)
          .filter((e) => IN_FLIGHT.has(e.status))
          .map((e) => e.id);
        if (!ids.length) {
          _statusTimer = null;
          return;
        }
        for (let i = 0; i < ids.length; i += STATUS_IDS_PER_CALL) {
          const asked = ids.slice(i, i + STATUS_IDS_PER_CALL);
          try {
            const response = await oliverClient.attachmentStatuses(asked);
            const answered = new Set();
            for (const row of response.data || []) {
              answered.add(row.id);
              upsert(commit, state, fromServer(row));
            }
            // A file the server no longer reports was deleted: stop following it.
            for (const id of asked) {
              if (!answered.has(id)) commit("REMOVE_ATTACHMENT", id);
            }
          } catch (err) {
            // A blip: the next tick asks again.
          }
        }
        _statusTimer = setTimeout(tick, STATUS_POLL_MS);
      };
      _statusTimer = setTimeout(tick, STATUS_POLL_MS);
    },

    /**
     * Resolve once Oliver is no longer generating for this chat.
     *
     * Asks the server rather than inferring it from "the last message is still
     * mine" — that is equally true of a turn that died days ago, and guessing
     * shows a waiting indicator for a reply that is never coming.
     */
    watchGeneration({ dispatch }, { chatId }) {
      if (_generationWatchers[chatId])
        return _generationWatchers[chatId].promise;

      let progressSeen = null;
      let progressedAt = Date.now();
      let failures = 0;
      let finished = false;
      const entry = {};
      const watch = (async () => {
        try {
          for (;;) {
            let status = null;
            try {
              const { data } = await oliverClient.getGenerationStatus(chatId);
              status = data || {};
              failures = 0;
            } catch (err) {
              // A failed probe says nothing about the turn — and the server
              // restarting is exactly when it fails. Keep waiting through a
              // blip, give up only once it looks genuinely gone.
              if (++failures > GENERATION_PROBE_FAILURE_LIMIT) return false;
              await new Promise((r) =>
                setTimeout(r, GENERATION_POLL_INTERVAL_MS)
              );
              continue;
            }
            if (!status.running) {
              finished = true;
              return { followUpsPending: !!status.follow_ups_pending };
            }
            const progress = JSON.stringify(status.progress ?? null);
            if (progress !== progressSeen) {
              progressSeen = progress;
              progressedAt = Date.now();
            }
            if (Date.now() - progressedAt >= GENERATION_WATCH_CEILING_MS)
              return false;
            entry.status = status;
            dispatch("showPendingReply", { chatId, status });
            await new Promise((r) =>
              setTimeout(r, GENERATION_POLL_INTERVAL_MS)
            );
          }
        } finally {
          // Only if this watch still owns the slot: a later one may already
          // have replaced it, and clearing then would take down an indicator
          // that belongs to a watch which is still running.
          // A finished turn's caller reloads the chat first, so its answer replaces the
          // placeholder rather than vanishing and reappearing.
          if (_generationWatchers[chatId] === entry) {
            delete _generationWatchers[chatId];
            if (!finished) dispatch("clearPendingReply", chatId);
          }
        }
      })();

      entry.promise = watch;
      _generationWatchers[chatId] = entry;
      return watch;
    },

    /**
     * Show that a reply is coming, using the very same assistant-turn placeholder
     * a live turn renders. Reopening a conversation mid-answer has no stream and
     * so no activity trail of its own; borrowing the live one keeps the wait
     * looking identical instead of inventing a second, lesser spinner.
     */
    showPendingReply({ commit, dispatch, state }, { chatId, status = null }) {
      // A turn found from the chat list belongs to a chat that may not be open yet.
      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("SET_PENDING_RECONNECT", { chatId, value: true });
      // Until its messages are loaded the chat shows only the spinner on its row: a reply
      // drawn into an empty chat reads as loaded, and its question would never be fetched.
      if (!state.chatStates[chatId].messages.length) return;
      const placed = state.chatStates[chatId].messages.some(
        (m) => m.id === PENDING_REPLY_ID
      );
      // The chat reloaded under the reply: its text is typed again from the start.
      if (!placed) dispatch("stopResumedTyping", chatId);
      commit("SHOW_RESUMED_TURN", {
        chatId,
        progress: status?.progress,
        startedAt: status?.started_at ? Date.parse(status.started_at) : null,
      });
      const text = status?.progress?.text || "";
      const typing = (_resumeTypers[chatId] ||= {
        pushed: "",
        typer: createTyper((shown) => {
          // Never re-adds the reply: a chat that reloaded under it drops it until the next check.
          const messages = state.chatStates[chatId]?.messages || [];
          if (!messages.some((m) => m.id === PENDING_REPLY_ID)) return;
          commit("UPDATE_CHAT_MESSAGE", {
            chatId,
            message: { id: PENDING_REPLY_ID, content: shown, replace: true },
          });
        }, RESUMED_TYPING_FRAMES),
      });
      if (
        text.length > typing.pushed.length &&
        text.startsWith(typing.pushed)
      ) {
        typing.typer.push(text.slice(typing.pushed.length));
        typing.pushed = text;
      }
    },

    stopResumedTyping(_ctx, chatId) {
      _resumeTypers[chatId]?.typer.stop();
      delete _resumeTypers[chatId];
    },

    /**
     * Type out the rest of a finished resumed reply, as the saved answer has it, so the reload
     * that follows does not drop it in all at once.
     */
    async finishResumedTyping({ dispatch }, chatId) {
      const typing = _resumeTypers[chatId];
      if (!typing) return;
      try {
        const { data } = await oliverClient.getMessages(chatId, { limit: 5 });
        const answer = (data?.data || [])
          .filter((m) => m.role === "assistant")
          .sort(
            (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
          )[0];
        const content = answer?.content || "";
        if (content.startsWith(typing.pushed)) {
          typing.typer.push(content.slice(typing.pushed.length));
          typing.pushed = content;
        }
        await Promise.race([
          typing.typer.idle(),
          new Promise((r) => setTimeout(r, FINAL_TYPING_LIMIT_MS)),
        ]);
        typing.typer.flush();
      } catch (err) {
        // The reload that follows shows the answer either way; only the typing is lost.
      } finally {
        dispatch("stopResumedTyping", chatId);
      }
    },

    /** Take the placeholder back down. Safe to call when it was never shown. */
    clearPendingReply({ commit, dispatch }, chatId) {
      dispatch("stopResumedTyping", chatId);
      commit("SET_PENDING_RECONNECT", { chatId, value: false });
      commit("DELETE_CHAT_MESSAGE", { chatId, messageId: PENDING_REPLY_ID });
    },

    /**
     * Reach Oliver again after losing the stream, and report what it says.
     *
     * A probe that fails outright is not an answer — a restarting pod is
     * exactly why we are here — so those are retried. A probe that comes back
     * with an HTTP status IS an answer: the service is up, something else is
     * wrong, and waiting longer will not help.
     *
     * @returns {{running: boolean}|null} null if Oliver stayed unreachable.
     */
    async probeOliver(_ctx, { chatId }) {
      const deadline = Date.now() + OLIVER_UNREACHABLE_BUDGET_MS;
      for (;;) {
        try {
          const { data } = await oliverClient.getGenerationStatus(chatId);
          return {
            running: !!data?.running,
            followUpsPending: !!data?.follow_ups_pending,
          };
        } catch (err) {
          // A gateway error is the pod still coming up, so keep waiting. Any
          // other status means Oliver answered for itself and waiting is futile.
          const status = err?.response?.status;
          if (status && !isUpstreamUnavailable(status)) return null;
          if (Date.now() >= deadline) return null;
          await new Promise((r) => setTimeout(r, OLIVER_PROBE_RETRY_MS));
        }
      }
    },

    /**
     * The stream ended without finishing — typically Oliver restarting under
     * us. Work out whether the answer is still coming, already landed, or has
     * to be asked for again, and only tell the user something broke once none
     * of those is true.
     */
    async recoverFromConnectionLoss(
      { commit, dispatch, state },
      { chatId, messageId, userMessageId, attempt = 0 }
    ) {
      dispatch("showPendingReply", { chatId });

      // Only called once this turn is over one way or another, so it is also
      // where anything the user queued behind it gets released.
      const settle = () => {
        dispatch("clearPendingReply", chatId);
        commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
        dispatch("drainMessageQueue", chatId);
      };
      const giveUp = (reason) => {
        settle();
        commit("SET_MESSAGE_STATUS", {
          chatId,
          messageId: failingMessageId(state.chatStates[chatId], userMessageId),
          status: "failed",
          error: reason,
        });
      };

      // 1. Is Oliver answering at all yet?
      const status = await dispatch("probeOliver", { chatId });
      if (!status) {
        giveUp("Lost connection to Oliver");
        return;
      }

      // 2. A turn is still in flight — ours outlived the dropped stream, or
      //    the row is simply not stale yet. Wait it out; asking now would only
      //    be refused. Deliberately falls through rather than returning: the
      //    wait ends either because the turn finished OR because its heartbeat
      //    went quiet, and only step 3 can tell those apart.
      let followUpsPending = status.followUpsPending;
      if (status.running) {
        const watched = await dispatch("watchGeneration", { chatId });
        followUpsPending = !!watched?.followUpsPending;
      }

      // 3. Nothing is running now. The answer may have been saved just before
      //    we lost contact, or by the turn we just waited on. Reloading also
      //    replaces the half-streamed bubble with what was actually stored, so
      //    a retry can never leave a partial answer sitting above the real one.
      await dispatch("getChatMessages", {
        chatId,
        limit: MESSAGES_PAGE,
        silent: true,
      });
      if (lastRole(state.chatStates[chatId]) === "assistant") {
        settle();
        if (followUpsPending) dispatch("awaitFollowUps", { chatId });
        return;
      }

      // 4. Still unanswered, so ask again. The graph checkpoint is keyed by
      //    conversation, so this resumes the turn rather than starting it over,
      //    and the user's message is already stored — nothing is re-sent.
      if (attempt >= LOST_STREAM_RETRY_LIMIT) {
        giveUp("Oliver stopped responding");
        return;
      }
      dispatch("clearPendingReply", chatId);
      commit("SET_WAITING_FOR_RESPONSE", { chatId, value: true });
      dispatch("establishSSEConnection", {
        chatId,
        messageId,
        userMessageId,
        retryAttempt: attempt + 1,
      });
    },

    /**
     * Recover from a 409 on the answer stream. The user's message is already
     * persisted, so this waits out the in-flight turn and then requests the
     * answer for that same message rather than sending anything again.
     */
    async resumeAfterConflict(
      { dispatch },
      { chatId, messageId, userMessageId }
    ) {
      const finished = await dispatch("watchGeneration", { chatId });
      if (!finished) return;

      // Pick up whatever the turn we were waiting on produced, so its reply is
      // on screen before ours starts streaming underneath it.
      await dispatch("getChatMessages", {
        chatId,
        limit: MESSAGES_PAGE,
        silent: true,
      });
      dispatch("clearPendingReply", chatId);
      dispatch("establishSSEConnection", { chatId, messageId, userMessageId });
    },

    /**
     * Called when a conversation is opened whose last message is still the
     * user's. A reply may be generating in the background from a previous
     * visit — show the wait and load it when it lands.
     */
    resumePendingGeneration({ dispatch, state }, { chatId }) {
      // This tab is streaming the turn itself: following it too would draw a second reply.
      const cs = state.chatStates[chatId];
      if (
        state.activeStreams[chatId] ||
        cs?.isWaitingForResponse ||
        cs?.isStreamingContent
      )
        return;
      // A chat opened mid-turn draws the reply from the last check now, not at the next one.
      const status = _generationWatchers[chatId]?.status;
      if (status) dispatch("showPendingReply", { chatId, status });
      if (!_resumes[chatId]) {
        _resumes[chatId] = dispatch("followResumedTurn", { chatId }).finally(
          () => delete _resumes[chatId]
        );
      }
      return _resumes[chatId];
    },

    async followResumedTurn({ commit, dispatch, state }, { chatId }) {
      const finished = await dispatch("watchGeneration", { chatId });
      if (finished) {
        await dispatch("finishResumedTyping", chatId);
        await dispatch("getChatMessages", {
          chatId,
          limit: MESSAGES_PAGE,
          silent: true,
        });
        dispatch("clearPendingReply", chatId);
        if (state.selectedChat?.id !== chatId) {
          commit("SET_UNSEEN_COMPLETION", { chatId, value: true });
        }
      }
      // Nothing is watching this conversation any more, so anything the user
      // queued behind the reply has to be released — otherwise it waits on a
      // turn that is already over.
      dispatch("drainMessageQueue", chatId);
      if (finished?.followUpsPending) dispatch("awaitFollowUps", { chatId });
    },

    /**
     * A finished answer's chips come seconds after it. A chat that was not streaming it shows
     * them loading under the answer, waits for the server to say they are made, then reloads.
     */
    async awaitFollowUps({ commit, dispatch, state }, { chatId }) {
      const messages = state.chatStates[chatId]?.messages || [];
      const answer = [...messages]
        .reverse()
        .find((m) => m.role === "assistant");
      if (!answer || answer.followUps?.length) return;
      const messageId = answer.server_id || answer.id;
      commit("SET_MESSAGE_FOLLOW_UPS_PENDING", {
        chatId,
        messageId,
        pending: true,
      });
      const deadline = Date.now() + FOLLOW_UPS_WAIT_MS;
      try {
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, GENERATION_POLL_INTERVAL_MS));
          const { data } = await oliverClient.getGenerationStatus(chatId);
          if (!data?.follow_ups_pending) break;
        }
        await dispatch("getChatMessages", {
          chatId,
          limit: MESSAGES_PAGE,
          silent: true,
        });
      } catch (err) {
        // Chips are optional: a failed check leaves the answer as it is, with none.
      } finally {
        commit("SET_MESSAGE_FOLLOW_UPS_PENDING", {
          chatId,
          messageId,
          pending: false,
        });
      }
    },

    /**
     * Get list of conversations
     * Uses skip/limit pagination (Oliver API) but maintains page-based interface for compatibility
     */
    async getChats({ commit, dispatch, state }, payload = {}) {
      commit("SET_CHAT_HISTORY_LOADING", true);

      try {
        const limit = payload.limit || 50;
        const page = payload.page || 1;
        const title = payload.title || "";
        const skip = (page - 1) * limit;

        // Fetch one extra item so we can tell whether another page exists
        // instead of guessing from `results.length === limit`, which is
        // wrong whenever the total is an exact multiple of the page size.
        const response = await oliverClient.getConversations({
          skip,
          limit: limit + 1,
          title,
        });
        const hasMore = response.data.length > limit;
        const conversations = hasMore
          ? response.data.slice(0, limit)
          : response.data;

        if (page === 1) {
          commit("SET_CHAT_HISTORY_PAGINATION", {
            total: conversations.length,
            page: page,
            itemsPerPage: limit,
            data: conversations,
            skip: skip,
            hasMore,
          });
        } else {
          commit("SET_CHAT_HISTORY_PAGINATION", {
            total: state.chatHistory.pagination.total + conversations.length,
            page: page,
            itemsPerPage: limit,
            data: uniqBy(
              [...state.chatHistory.pagination.data, ...conversations],
              "id"
            ),
            skip: skip,
            hasMore,
          });
        }

        commit("SET_CHAT_HISTORY_LOADING", false);
        // A turn still running from before a reload shows on its row without opening the chat.
        conversations
          .filter((conversation) => conversation.generating)
          .forEach((conversation) =>
            dispatch("resumePendingGeneration", { chatId: conversation.id })
          );
        return { data: { data: conversations } };
      } catch (err) {
        commit("SET_CHAT_HISTORY_LOADING", false);
        // `silent`: the background reconnect refresh must not pop a modal.
        if (!payload.silent && err.response?.status !== 403) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.detail || "Something went wrong...",
            icon: "error",
          });
        }
        return false;
      }
    },

    /**
     * Get the last conversation or create a new one
     * Uses last-or-new endpoint to reuse empty/recent conversations
     */
    async createChat({ commit }) {
      commit("SET_CREATING_CHAT", true);

      try {
        const response = await oliverClient.getOrCreateConversation();
        commit("SET_CREATING_CHAT", false);
        commit("ADD_CHAT_TO_HISTORY", response.data);
        return { data: { data: response.data } };
      } catch (err) {
        commit("SET_CREATING_CHAT", false);
        if (err.response?.status !== 403) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.detail || "Something went wrong...",
            icon: "error",
          });
        }
        return false;
      }
    },

    /**
     * Get messages for a conversation with cursor-based pagination
     */
    async getChatMessages({ commit, dispatch, state }, payload) {
      const chatId = payload.chatId;

      // A full reload (no cursor) would replace the chat's messages with server
      // data that does not yet include an in-flight streaming answer. Skip it
      // while a response is being generated so background output isn't lost.
      const cs = state.chatStates[chatId];
      if (
        !payload.cursor &&
        (state.activeStreams[chatId] ||
          cs?.isWaitingForResponse ||
          cs?.isStreamingContent)
      ) {
        return false;
      }

      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: true });

      try {
        const limit = payload.limit || 10;
        const cursor = payload.cursor || null;

        const response = await oliverClient.getMessages(chatId, {
          limit,
          cursor,
        });

        const chatState = state.chatStates[chatId];
        // Re-hydrate persisted cards from history, answered when a later message answered them.
        const ordered = sortBy([...(response.data.data || [])], ["created_at"]);
        const messages = ordered.map((m, idx) => {
          // Guardrail refusals persist their card payload in context_metadata
          // so the boundary styling + route card re-render on reload.
          if (m?.context_metadata?.guardrail && m.role === "assistant") {
            m = { ...m, guardrail: m.context_metadata.guardrail };
          }
          if (m?.role === "assistant" && m.context_metadata) {
            const { citations, calculation, trail, timing } =
              m.context_metadata;
            if (citations || calculation) {
              m = {
                ...m,
                ...(citations && { citations }),
                ...(calculation && { calculation }),
              };
            }
            const activity = savedActivity(trail, timing);
            if (activity) m = { ...m, activity };
            const followUps = m.context_metadata.follow_ups;
            if (Array.isArray(followUps) && followUps.length) {
              m = { ...m, followUps };
            }
          }
          const persisted = m?.context_metadata?.interaction;
          if (!Array.isArray(persisted) || !persisted.length) return m;
          const interactionResolution = resolutionOf(
            persisted,
            ordered.slice(idx + 1)
          );
          return { ...m, interactions: persisted, interactionResolution };
        });
        const meta = response.data.meta;
        dispatch("followAttachments", messages);

        if (cursor) {
          commit("SET_CHAT_MESSAGES_PAGINATION", {
            chatId,
            nextCursor: meta.next_cursor,
            limit: limit,
            data: [
              ...sortBy([...messages], ["created_at"]),
              ...(chatState?.messages || []),
            ],
          });
        } else {
          commit("SET_CHAT_MESSAGES_PAGINATION", {
            chatId,
            nextCursor: meta.next_cursor,
            limit: limit,
            data: sortBy(messages, ["created_at"]),
          });
        }

        commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: false });
        return response;
      } catch (err) {
        commit("SET_CHAT_MESSAGES_LOADING", { chatId, loading: false });
        // `silent`: a restore's id can be stale or foreign, where a 404 is expected.
        if (!payload.silent && err.response?.status !== 403) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.detail || "Something went wrong...",
            icon: "error",
          });
        }
        return false;
      }
    },

    /**
     * Send a message and stream the AI response. Its files are named by `attachment_ids`:
     * they were created before their upload and bind whatever state they are in.
     */
    async sendChatMessage({ commit, dispatch, state }, payload) {
      const chatId = payload.chatId;
      const userMessageId = payload.userMessageId;
      const shouldGenerateTitle = payload.generateTitle || false;

      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("SET_WAITING_FOR_RESPONSE", { chatId, value: true });
      commit("SET_STREAMING_CONTENT", { chatId, value: false });

      const activePatientMode =
        payload.patientModeOverride !== undefined
          ? payload.patientModeOverride
          : state.patientMode;
      const mode = activePatientMode?.id ? "patient" : null;
      const contextMetadata = patientContextMetadata(activePatientMode);

      // Explicit patient-driven quick summaries only: honor the flag on the
      // active patient snapshot and DO NOT rely on message text heuristics.
      if (contextMetadata && activePatientMode?.quick_summary) {
        contextMetadata.quick_summary = true;

        // If this quick_summary came from the stored `patientMode` (i.e. no
        // patientModeOverride was provided), clear the flag so it applies to
        // only this outgoing message.
        if (payload.patientModeOverride === undefined) {
          try {
            const newPatient = { ...activePatientMode };
            delete newPatient.quick_summary;
            await dispatch("setPatientMode", newPatient);
          } catch (err) {
            // non-fatal: continue without clearing the flag
            console.warn(
              "Failed to clear quick_summary flag on patientMode:",
              err
            );
          }
        }
      }

      dispatch("clearPatientModeOverride", { chatId });

      // Until the server has the message, its files are bound to nothing.
      let messageCreated = false;
      try {
        const attachmentIds = payload.data.attachment_ids || [];

        const finalContextMetadata = payload.extraMetadata
          ? { ...(contextMetadata || {}), ...payload.extraMetadata }
          : contextMetadata;

        const messageResponse = await oliverClient.createMessage(chatId, {
          content: payload.data.content,
          role: contextMetadata?.quick_summary ? "assistant" : "user",
          mode,
          contextMetadata: finalContextMetadata,
          webAccess: state.webAccess,
          attachmentIds: attachmentIds.length ? attachmentIds : undefined,
        });

        const serverMessageId = messageResponse.data?.id;
        if (!serverMessageId) {
          console.error("No message ID in response:", messageResponse.data);
          throw new Error("Failed to get message ID from server response");
        }

        messageCreated = true;
        commit("SET_MESSAGE_STATUS", {
          chatId,
          messageId: userMessageId,
          status: "sent",
        });

        const streamPromise = dispatch("establishSSEConnection", {
          chatId,
          messageId: serverMessageId,
          userMessageId,
          userMessageAttachments: messageResponse.data?.attachments || [],
        });

        if (shouldGenerateTitle) {
          dispatch("generateChatTitle", chatId);
        }

        await streamPromise;

        return {
          data: {
            data: messageResponse.data,
          },
        };
      } catch (err) {
        commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
        commit("SET_MESSAGE_STATUS", {
          chatId,
          messageId: userMessageId,
          status: "failed",
          error: err?.response?.data?.detail || "Failed to send message",
        });
        if (!messageCreated) {
          // Not sent, so the files go back to the composer rather than being lost.
          dispatch("returnToComposer", {
            chatId,
            attachments: (payload.data.attachment_ids || []).map((id) => ({
              id,
            })),
          });
        }
        return false;
      }
    },

    /**
     * Answer a card: mark it answered, show the answer as the clinician's message, and send
     * it with the answer in context_metadata so the next turn reads it.
     */
    async answerInteraction(
      { dispatch, commit, state },
      { chatId, text, response }
    ) {
      chatId = chatId || state.selectedChat?.id;
      if (!chatId || !text || !response) return false;

      const card = state.chatStates[chatId]?.messages?.find((m) =>
        (m.interactions || []).some(
          (g) => g && g.id === response.interaction_id
        )
      );
      if (card) {
        commit("UPDATE_CHAT_MESSAGE", {
          chatId,
          message: {
            id: card.id,
            interactionResolution: { selections: response.values.selections },
            replace: true,
          },
        });
      }

      const userMessageId = `user-${Date.now()}`;
      await dispatch("storeChatMessage", {
        chatId,
        message: {
          id: userMessageId,
          role: "user",
          content: text,
          created_at: new Date().toISOString(),
        },
      });

      return dispatch("sendChatMessage", {
        chatId,
        userMessageId,
        data: { content: text },
        extraMetadata: { interaction_response: response },
      });
    },

    /**
     * Establish SSE connection to stream AI response
     * Uses POST-based streaming with fetch API
     */
    async establishSSEConnection(
      { commit, state, dispatch },
      {
        chatId,
        messageId,
        userMessageId,
        userMessageAttachments = [],
        retryAttempt = 0,
      }
    ) {
      commit("INITIALIZE_CHAT_STATE", chatId);

      const assistantMessageId = `assistant-${Date.now()}`;

      const processNextQueued = () => dispatch("drainMessageQueue", chatId);

      const controller = new AbortController();
      commit("SET_ACTIVE_STREAM", {
        chatId,
        controller,
        messageId: assistantMessageId,
      });

      // The reasoning block exists from here on, so the wait is legible from the
      // click rather than from whenever the first tool happens to start.
      commit("BEGIN_TURN_ACTIVITY", { chatId, messageId: assistantMessageId });

      let textStarted = false;
      // Everything handed to the typer so far, to compare with the saved answer.
      let pushedText = "";
      // The saved id of the finished answer, once `complete` has named it.
      let completedId = null;
      let skeletonTimer = null;
      const cancelSkeletonTimer = () => {
        clearTimeout(skeletonTimer);
        skeletonTimer = null;
      };

      // Spread over a fixed number of frames so it never falls behind the stream.
      const TYPING_FRAMES = 12;
      const typed = createTyper(
        (text) =>
          commit("UPDATE_CHAT_MESSAGE", {
            chatId,
            message: {
              id: assistantMessageId,
              role: "assistant",
              content: text,
              replace: true,
              created_at: new Date().toISOString(),
            },
          }),
        TYPING_FRAMES
      );

      return new Promise((resolve) => {
        oliverClient.streamAnswer(
          chatId,
          messageId,
          {
            onMetadata: () => {
              commit("SET_ACTIVITY_PHASE", {
                chatId,
                messageId: assistantMessageId,
                phase: "starting",
              });
            },

            onActivityStep: (data) => {
              if (!data?.id) return;

              // Work resumed after text: the next content opens a new stretch, so it is
              // shown as writing rather than under a still-running chip.
              textStarted = false;
              commit("SET_STREAMING_CONTENT", { chatId, value: false });

              commit("UPSERT_ACTIVITY_STEP", {
                chatId,
                messageId: assistantMessageId,
                step: data,
              });
              commit("SET_ACTIVITY_PHASE", {
                chatId,
                messageId: assistantMessageId,
                phase: "tool",
              });
            },

            // The turn's artifact ids, positionally: the answer writes
            // `attachment:2` and this is what says what 2 is. Arrives before
            // the first token, so a chart can resolve and render while the
            // answer is still being written rather than only once it ends.
            onArtifacts: (data) => {
              const artifacts = data?.artifacts || [];
              if (!artifacts.length) return;
              commit("UPDATE_CHAT_MESSAGE", {
                chatId,
                message: {
                  id: assistantMessageId,
                  role: "assistant",
                  artifacts,
                  replace: true,
                  created_at: new Date().toISOString(),
                },
              });
            },
            // The stream stays open after `complete` while chips are made; however it
            // ends, nothing is left pending.
            onEnd: () => {
              cancelSkeletonTimer();
              commit("SET_MESSAGE_FOLLOW_UPS_PENDING", {
                chatId,
                messageId: completedId,
                pending: false,
              });
            },
            onFollowUps: (data) => {
              cancelSkeletonTimer();
              const followUps = data?.follow_ups || [];
              if (!followUps.length) return;
              commit("SET_MESSAGE_FOLLOW_UPS", {
                chatId,
                messageId: data.message_id,
                followUps,
              });
            },

            onContent: (data) => {
              const content = data.content;
              if (content) {
                commit("SET_STREAMING_CONTENT", { chatId, value: true });
                // Once per stretch of text, not once per token: text streams in
                // hundreds of deltas and none of this changes after the first.
                if (!textStarted) {
                  textStarted = true;
                  commit("SET_ACTIVITY_PHASE", {
                    chatId,
                    messageId: assistantMessageId,
                    phase: "writing",
                  });
                }

                pushedText += content;
                typed.push(content);
              }
            },

            onInteraction: (data) => {
              // `replace: true` is required so `interactions` merges onto the
              // message; default mode only appends streamed content.
              const cards = data?.interaction || [];
              if (cards.length) {
                commit("UPDATE_CHAT_MESSAGE", {
                  chatId,
                  message: {
                    id: assistantMessageId,
                    role: "assistant",
                    interactions: cards,
                    replace: true,
                    created_at: new Date().toISOString(),
                  },
                });
              }
            },

            onGuardrail: (data) => {
              // Guardrail boundary metadata (safety/medication/legal route
              // card or clinical-judgment redirect). The refusal text streams
              // via onContent; this only decorates the message so the bubble
              // renders the boundary styling + route card.
              if (!data) return;
              commit("UPDATE_CHAT_MESSAGE", {
                chatId,
                message: {
                  id: assistantMessageId,
                  role: "assistant",
                  guardrail: data,
                  replace: true,
                  created_at: new Date().toISOString(),
                },
              });
            },

            onComplete: (data) => {
              // Compared against what was pushed, not what is painted: the typer may
              // still be catching up, and that is not a difference.
              const differs =
                data?.full_content !== undefined &&
                data.full_content !== pushedText;
              if (differs) typed.flush();
              if (data && (data.full_content || data.message_id)) {
                commit("UPDATE_CHAT_MESSAGE", {
                  chatId,
                  message: {
                    id: assistantMessageId,
                    role: "assistant",
                    // The real persisted id of this answer — lets the feedback
                    // control target it immediately, without a reload. The local
                    // `id` stays the temp id so the v-for key remains stable.
                    server_id: data.message_id || null,
                    ...(differs ? { content: data.full_content } : {}),
                    ...(data.citations ? { citations: data.citations } : {}),
                    ...(data.calculation
                      ? { calculation: data.calculation }
                      : {}),
                    attachments: userMessageAttachments,
                    replace: true,
                    created_at: new Date().toISOString(),
                  },
                });
              }

              if (data?.message_id) {
                completedId = data.message_id;
                skeletonTimer = setTimeout(() => {
                  skeletonTimer = null;
                  const streamed = (
                    state.chatStates[chatId]?.messages || []
                  ).find((m) => m.id === assistantMessageId);
                  if (
                    streamed &&
                    !streamed.guardrail &&
                    !streamed.interactions?.length &&
                    !streamed.followUps?.length
                  ) {
                    commit("SET_MESSAGE_FOLLOW_UPS_PENDING", {
                      chatId,
                      messageId: completedId,
                      pending: true,
                    });
                  }
                }, FOLLOW_UPS_SKELETON_DELAY_MS);
              }

              commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
              commit("SET_STREAMING_CONTENT", { chatId, value: false });
              commit("FINALIZE_MESSAGE_ACTIVITY", {
                chatId,
                messageId: assistantMessageId,
                outcome: "done",
              });
              commit("CLEAR_ACTIVE_STREAM", chatId);
              if (state.selectedChat?.id !== chatId) {
                commit("SET_UNSEEN_COMPLETION", { chatId, value: true });
              }
              processNextQueued();
              resolve(true);
            },

            onError: (error) => {
              typed.flush();
              if (pushedText) {
                commit("UPDATE_CHAT_MESSAGE", {
                  chatId,
                  message: {
                    id: assistantMessageId,
                    role: "assistant",
                    cutShort: true,
                    replace: true,
                    created_at: new Date().toISOString(),
                  },
                });
              }
              commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
              commit("SET_STREAMING_CONTENT", { chatId, value: false });
              commit("FINALIZE_MESSAGE_ACTIVITY", {
                chatId,
                messageId: assistantMessageId,
                outcome: "failed",
              });
              commit("CLEAR_ACTIVE_STREAM", chatId);

              // This attempt is being handed to recovery, so its own turn bubble
              // has nothing to say. Finalizing it above already hid the
              // reasoning line, which would otherwise leave a bare avatar
              // sitting in the thread. Anything it did stream is kept — recovery
              // reloads from the server before deciding what to do next.
              const dropEmptyTurn = () => {
                const turn = (state.chatStates[chatId]?.messages || []).find(
                  (m) => m.id === assistantMessageId
                );
                if (!turn?.content) {
                  commit("DELETE_CHAT_MESSAGE", {
                    chatId,
                    messageId: assistantMessageId,
                  });
                }
              };

              // Lost contact rather than a failed answer: the turn may still
              // be running, may have finished, or may need asking for again.
              // Deciding that here would guess; recovery goes and finds out.
              if (error?.connectionLost) {
                dropEmptyTurn();
                dispatch("recoverFromConnectionLoss", {
                  chatId,
                  messageId,
                  userMessageId,
                  attempt: retryAttempt,
                });
                resolve(false);
                return;
              }

              // 409 is not a failure: the conversation already had a turn in
              // flight, almost always a reply still generating elsewhere. This
              // message is already saved server-side, so re-sending it would
              // duplicate it — wait for the conversation to free up and ask for
              // this same message's answer again.
              if (error?.response?.status === 409) {
                dropEmptyTurn();
                dispatch("resumeAfterConflict", {
                  chatId,
                  messageId,
                  userMessageId,
                });
                resolve(false);
                return;
              }

              if (userMessageId) {
                commit("SET_MESSAGE_STATUS", {
                  chatId,
                  messageId: userMessageId,
                  status: "failed",
                  error: "Failed to receive AI response",
                });
              }

              processNextQueued();
              console.error("SSE connection error:", error);
              resolve(false);
            },
          },
          { signal: controller.signal }
        );
      });
    },

    /**
     * Generate a title for a conversation
     * Backend gets the first message and updates the conversation automatically
     */
    async generateChatTitle({ commit }, chatId) {
      try {
        const response = await oliverClient.generateTitle(chatId);
        const title = response.data;

        commit("SET_CHAT_TITLE", { chatId, title });

        return { data: { data: { title } } };
      } catch (err) {
        console.error("Failed to generate title:", err);
        return false;
      }
    },

    /**
     * Transcribe audio to text
     */
    async transcribeAudio({ commit }, payload) {
      commit("SET_TRANSCRIBING", true);

      try {
        const audioFile = payload.file.blob || payload.file;
        const response = await oliverClient.transcribeAudio(audioFile);

        commit("SET_TRANSCRIBING", false);
        return response.data;
      } catch (err) {
        commit("SET_TRANSCRIBING", false);
        if (err.response?.status !== 403) {
          Swal.fire({
            title: "Error",
            text: err?.response?.data?.detail || "Something went wrong...",
            icon: "error",
          });
        }
        return false;
      }
    },

    /**
     * Delete a conversation
     */
    async deleteChat({ commit, dispatch }, chatId) {
      try {
        dispatch("cancelChatStream", chatId);
        await oliverClient.deleteConversation(chatId);
        commit("REMOVE_CHAT_FROM_HISTORY", chatId);
        return true;
      } catch (err) {
        if (err.response?.status !== 403) {
          Swal.fire({
            title: "Error",
            text:
              err?.response?.data?.detail ||
              "Failed to delete chat. Please try again.",
            icon: "error",
          });
        }
        throw err;
      }
    },

    /**
     * Adopt the preferences stored on the account, on every auth/getUser.
     *
     * An account with nothing stored is a user who chose their appearance
     * before it moved off the browser, so their cached choice is adopted and
     * pushed up once. Runs before the widget mounts, so the migration is
     * invisible: the first frame is already the right colour.
     */
    async hydratePreferences({ commit, rootState }) {
      const preferences = rootState.auth?.user?.oliver_preferences;
      commit("SET_WEB_ACCESS", preferences?.web_access ?? DEFAULT_WEB_ACCESS);
      // Null-checked rather than falsy throughout: false is an explicit choice
      // of light mode, and `|| DEFAULT` would silently discard it.
      const storedTheme = preferences?.theme;
      const storedDarkMode = preferences?.dark_mode;

      if (storedTheme != null || storedDarkMode != null) {
        commit("SET_THEME", storedTheme ?? DEFAULT_THEME);
        commit("SET_DARK_MODE", storedDarkMode ?? DEFAULT_DARK_MODE);
        return;
      }

      const theme = loadFromStorage("oliverTheme", DEFAULT_THEME);
      const darkMode = loadFromStorage("oliverDarkMode", DEFAULT_DARK_MODE);

      commit("SET_THEME", theme);
      commit("SET_DARK_MODE", darkMode);

      try {
        await this.$http.put("user/oliver-preferences", {
          theme,
          dark_mode: darkMode,
        });
      } catch (err) {
        // Silent, unlike the setters below: the user did not ask for this, and
        // the cached values stay put for the next sign-in to retry.
        console.error("Failed to seed Oliver appearance:", err);
      }
    },

    /**
     * Optimistic + rollback, as the feedback actions above: a failed save puts
     * the UI back rather than showing a colour the account does not have. Sent
     * even when unchanged, so picking today's default still counts as a choice.
     */
    async setTheme({ commit, state }, theme) {
      const previous = state.theme;
      commit("SET_THEME", theme);

      try {
        await this.$http.put("user/oliver-preferences", {
          theme,
          dark_mode: state.darkMode,
        });
      } catch (err) {
        commit("SET_THEME", previous);
        if (!err?.accessDenied) {
          appearanceSaveFailed("Could not save your theme.");
        }
      }
    },

    /** Optimistic + rollback, exactly as setTheme above. */
    async setDarkMode({ commit, state }, value) {
      const previous = state.darkMode;
      commit("SET_DARK_MODE", value);

      try {
        await this.$http.put("user/oliver-preferences", {
          theme: state.theme,
          dark_mode: value,
        });
      } catch (err) {
        commit("SET_DARK_MODE", previous);
        if (!err?.accessDenied) {
          appearanceSaveFailed("Could not save your appearance setting.");
        }
      }
    },

    /** Optimistic + rollback, as setTheme above. Saved on its own: the appearance is untouched. */
    async setWebAccess({ commit, state }, value) {
      const previous = state.webAccess;
      commit("SET_WEB_ACCESS", value);

      try {
        await this.$http.put("user/oliver-preferences", { web_access: value });
      } catch (err) {
        commit("SET_WEB_ACCESS", previous);
        if (!err?.accessDenied) {
          appearanceSaveFailed("Could not save your web access setting.");
        }
      }
    },

    // ============ Simple state management actions (no API calls) ============

    setPatientMode({ commit }, mode) {
      commit("SET_PATIENT_MODE", mode);
    },

    /** Set a conversation's client mode deliberately; survives switching away. */
    setConversationPatientMode({ commit }, { chatId, patient }) {
      const next = patient || null;
      if (chatId)
        commit("SET_PATIENT_MODE_OVERRIDE", { chatId, patient: next });
      commit("SET_PATIENT_MODE", next);
      return next;
    },

    /** Drop the override once a sent message carries the mode. */
    clearPatientModeOverride({ commit }, { chatId }) {
      if (chatId) commit("CLEAR_PATIENT_MODE_OVERRIDE", chatId);
    },

    setSelectedChat({ commit }, payload) {
      if (payload?.id) {
        commit("SET_UNSEEN_COMPLETION", { chatId: payload.id, value: false });
      }
      commit("SET_SELECTED_CHAT", payload);
    },

    // Reopen the conversation named by `?oliverChat=`. Idempotent: the param stays in the
    // URL so a reload restores, which means this re-fires on every navigation.
    setRestoreRequest({ commit, state, dispatch }, { conversationId }) {
      // From the address bar, so validated rather than trusted.
      if (!isConversationId(conversationId)) return;
      if (
        state.restoreRequest?.conversationId === conversationId &&
        !state.restoreRequest?.consumed
      ) {
        return;
      }
      // Already looking at it — nothing to restore.
      if (state.selectedChat?.id === conversationId) return;

      commit("SET_RESTORE_REQUEST", {
        conversationId,
        status: "loading",
        consumed: false,
      });
      return dispatch("restoreConversation", { conversationId });
    },

    // Authorization is the messages endpoint's 404 — not /conversations/{id} or .../full,
    // which do not check ownership.
    async restoreConversation({ commit, dispatch, state }, { conversationId }) {
      const loaded = await dispatch("getChatMessages", {
        chatId: conversationId,
        limit: 50,
        silent: true,
      });

      // The user may have moved on while this was in flight.
      if (state.restoreRequest?.conversationId !== conversationId) return false;
      if (state.restoreRequest?.consumed) return false;

      // getChatMessages also returns false when it declines to overwrite a mid-stream
      // chat, where the messages are already present.
      const hasMessages = Boolean(
        state.chatStates[conversationId]?.messages?.length
      );
      if (!loaded && !hasMessages) {
        commit("SET_RESTORE_STATUS", "failed");
        return false;
      }

      await dispatch("setSelectedChat", { id: conversationId });
      commit("SET_RESTORE_STATUS", "ready");
      return true;
    },

    noteCitationNavigation({ commit }) {
      commit("NOTE_CITATION_NAVIGATION");
    },

    /** Stop restoring, whatever the URL still says. */
    consumeRestoreRequest({ commit }) {
      commit("CONSUME_RESTORE_REQUEST");
    },

    /**
     * Abort an in-flight answer stream for a chat and clear its waiting/streaming
     * flags. Intended for intentional cancellation (e.g. deleting a conversation),
     * NOT for chat switches — switching must keep streams running in the background.
     */
    cancelChatStream({ commit, state }, chatId) {
      const active = state.activeStreams[chatId];
      if (active) {
        active.controller.abort();
        commit("CLEAR_ACTIVE_STREAM", chatId);
        // Abort skips onComplete/onError, so finalize the trail here — otherwise
        // its line keeps working forever.
        if (active.messageId) {
          commit("FINALIZE_MESSAGE_ACTIVITY", {
            chatId,
            messageId: active.messageId,
            outcome: "cancelled",
          });
        }
      }
      if (state.chatStates[chatId]) {
        commit("SET_WAITING_FOR_RESPONSE", { chatId, value: false });
        commit("SET_STREAMING_CONTENT", { chatId, value: false });
      }
    },

    storeChatMessage({ commit, state }, payload) {
      const chatId = payload.chatId;
      commit("INITIALIZE_CHAT_STATE", chatId);
      commit("STORE_CHAT_MESSAGE", {
        chatId,
        message: payload.message,
      });
      return state.chatStates[chatId]?.messages?.at(-1);
    },

    updateChatMessage({ commit }, payload) {
      const chatId = payload.chatId;
      commit("UPDATE_CHAT_MESSAGE", {
        chatId,
        message: {
          ...payload.updates,
          replace: true,
        },
      });
    },

    resetChatMessages({ commit }, chatId) {
      commit("RESET_CHAT_MESSAGES", chatId);
    },

    /**
     * Flag an assistant message as not useful (negative-only feedback).
     * Optimistically marks the message flagged, then persists. On failure the
     * optimistic change is rolled back and the error is rethrown so the caller
     * can surface a non-blocking message — the chat itself is never broken.
     */
    async submitMessageFeedback(
      { commit, state },
      { chatId, messageId, reasons, comment }
    ) {
      const messages = state.chatStates[chatId]?.messages || [];
      const previous =
        messages.find(
          (msg) => msg.id == messageId || msg.server_id == messageId
        )?.feedback ?? null;

      commit("SET_MESSAGE_FEEDBACK", {
        chatId,
        messageId,
        feedback: { reasons, comment: comment || null, rating: "negative" },
      });

      try {
        const response = await oliverClient.submitMessageFeedback(
          chatId,
          messageId,
          { reasons, comment }
        );
        // Reconcile with the authoritative server record.
        commit("SET_MESSAGE_FEEDBACK", {
          chatId,
          messageId,
          feedback: response.data,
        });
        return response.data;
      } catch (err) {
        commit("SET_MESSAGE_FEEDBACK", {
          chatId,
          messageId,
          feedback: previous,
        });
        throw err;
      }
    },

    /**
     * Remove (undo) the user's feedback on an assistant message.
     * Same optimistic + rollback semantics as submitMessageFeedback.
     */
    async removeMessageFeedback({ commit, state }, { chatId, messageId }) {
      const messages = state.chatStates[chatId]?.messages || [];
      const previous =
        messages.find(
          (msg) => msg.id == messageId || msg.server_id == messageId
        )?.feedback ?? null;

      commit("SET_MESSAGE_FEEDBACK", { chatId, messageId, feedback: null });

      try {
        await oliverClient.removeMessageFeedback(chatId, messageId);
      } catch (err) {
        commit("SET_MESSAGE_FEEDBACK", {
          chatId,
          messageId,
          feedback: previous,
        });
        throw err;
      }
    },

    /** Send the next queued message, if the queue is open and has one. */
    drainMessageQueue({ state, dispatch }, chatId) {
      const chatState = state.chatStates[chatId];
      if (
        !chatState ||
        chatState.isQueuePaused ||
        chatState.messageQueue.length === 0
      )
        return;
      dispatch("dequeueAndSend", { chatId });
    },

    dequeueAndSend({ commit, state, dispatch }, { chatId }) {
      const chatState = state.chatStates[chatId];
      if (!chatState || chatState.messageQueue.length === 0) return;

      const next = chatState.messageQueue[0];
      commit("DEQUEUE_MESSAGE", { chatId });

      const nextUserMessageId = `user-${Date.now()}`;
      dispatch("storeChatMessage", {
        chatId,
        message: {
          id: nextUserMessageId,
          role: "user",
          content: next.text,
          attachments: next.attachments || [],
          created_at: new Date().toISOString(),
        },
      });

      dispatch("sendChatMessage", {
        chatId,
        userMessageId: nextUserMessageId,
        generateTitle: false,
        patientModeOverride: next.patientModeSnapshot,
        data: { content: next.text, attachment_ids: next.attachment_ids || [] },
      });
    },
  },
};
